'use client';
import { isKaskoInsuranceSlug } from '@/utils/kaskoInsurance';
import { isTpvcaMandatorySlug } from '@/utils/tpvcaInsurance';
import { useRouter, useParams } from 'next/navigation';
import { withLocalizedHref } from '@/lib/localizedRoutes.mjs';
import { IconFlagEu } from '@/assets/icons';
import CustomDatePicker from '@/components/ui/CustomDatePicker';
import Checkbox from '@/components/ui/Checkbox';
import { useLanguage } from '@/context/LanguageContext';
import styles from '../Step1BasicData.module.css';
import { isSpecialMachineryInsuranceSlug } from '@/utils/specialMachineryInsurance';
import { writeStoredCalculatorStep } from '@/utils/insuranceStorageCleanup';

export default function VehicleFormFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  setValidationErrors,
  fields,
  plateError,
  vinError,
  personalCodeError,
  handlePlateChange,
  handleVinChange,
  handlePersonalCodeChange,
  slug,
  onlyPrimaryFields = false,
  hideOwnerCode = false,
  hideRegisteredVehicleToggle = false,
}) {
  const { t, locale } = useLanguage();
  const ownerCodeMaxLength =
    isTpvcaMandatorySlug(slug) ||
    slug === 'neregistruoto-automobilio-draudimas-internetu'
      ? 11
      : 12;
  const router = useRouter();
  const params = useParams();

  const plateField =
    formData.registeredVehicle === true && fields.includes('plate') ? (
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.step1.license_plate')}
        </label>
        <div
          className={`${styles.inputWithIcon} ${plateError || validationErrors.plate ? styles.inputError : ''}`}
        >
          <span className={styles.flag}>
            <IconFlagEu />
          </span>
          <input
            className={`${styles.input} ${plateError || validationErrors.plate ? styles.inputError : ''}`}
            type='text'
            placeholder={t('calculator.step1.license_plate_placeholder')}
            value={formData.plate || ''}
            onChange={handlePlateChange}
            maxLength={6}
          />
        </div>
        <div
          key={`plate-error-${(plateError || validationErrors.plate || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.plate ? styles.errorHighlight : ''}`}
        >
          {plateError || validationErrors.plate || ''}
        </div>
      </div>
    ) : null;

  const ownerCodeField = (
    <div className={styles.formGroup}>
      <label className={styles.label}>
        {t('calculator.step1.owner_code')}
      </label>
      <input
        className={`${styles.input} ${personalCodeError || validationErrors.code ? styles.inputError : ''}`}
        type='text'
        placeholder={t('calculator.step1.owner_code_placeholder')}
        value={formData.code || ''}
        onChange={handlePersonalCodeChange}
        maxLength={ownerCodeMaxLength}
      />
      <div
        key={`code-error-${(personalCodeError || validationErrors.code || '').length}`}
        className={`${styles.fieldError} ${showValidationErrors && validationErrors.code ? styles.errorHighlight : ''}`}
      >
        {personalCodeError || validationErrors.code || ''}
      </div>
    </div>
  );

  if (onlyPrimaryFields) {
    return (
      <>
        {plateField}
        {hideOwnerCode ? null : ownerCodeField}
      </>
    );
  }

  const handleRegisteredVehicleChange = newValue => {
    // Update form data first
    setFormData(prev => ({ ...prev, registeredVehicle: newValue }));

    // Check if we need to redirect based on insurance type
    const currentSlug = params?.insurance || slug;

    if (
      newValue === false &&
      isTpvcaMandatorySlug(currentSlug)
    ) {
      // Redirect to unregistered vehicle insurance
      writeStoredCalculatorStep('neregistruoto-automobilio-draudimas-internetu', 0);
      router.push(
        withLocalizedHref('/skaiciuokle/neregistruoto-automobilio-draudimas-internetu', locale)
      );
    } else if (
      newValue === true &&
      currentSlug === 'neregistruoto-automobilio-draudimas-internetu'
    ) {
      writeStoredCalculatorStep('automobilio-privalomasis-draudimas-internetu', 0);
      router.push(
        withLocalizedHref('/skaiciuokle/automobilio-privalomasis-draudimas-internetu', locale)
      );
    }
  };

  return (
    <>
      {plateField}

      {/* VIN — required for unregistered insurance; optional for other flows */}
      {(slug === 'neregistruoto-automobilio-draudimas-internetu' ||
        fields.includes('vin') ||
        (fields.includes('plate') && formData.registeredVehicle === false)) && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('calculator.step1.vin_number')}
          </label>
          <div
            className={`${styles.inputWithIcon} ${vinError || validationErrors.vin ? styles.inputError : ''}`}
          >
            <span className={styles.flag}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M7.91682 2.5L5.41682 17.5M14.5835 2.5L12.0835 17.5M17.0835 6.66667H2.91683M16.2502 13.3333H2.0835'
                  stroke='#9AA4B2'
                  strokeWidth='1.66667'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
            <input
              className={`${styles.input} ${vinError || validationErrors.vin ? styles.inputError : ''}`}
              type='text'
              placeholder={t('calculator.step1.vin_placeholder')}
              value={formData.vin || ''}
              onChange={handleVinChange}
              maxLength={17}
            />
          </div>
          <div
            key={`vin-error-${(vinError || validationErrors.vin || '').length}`}
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.vin ? styles.errorHighlight : ''}`}
          >
            {vinError || validationErrors.vin || ''}
          </div>
        </div>
      )}

      {hideOwnerCode ? null : ownerCodeField}

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.step1.start_date')}
        </label>
        <CustomDatePicker
          title={t('calculator.step1.start_date')}
          value={formData.startDate}
          onChange={formatted => {
            setFormData(prev => ({ ...prev, startDate: formatted }));
            // Clear validation error when date changes
            if (
              formatted &&
              validationErrors.startDate &&
              setValidationErrors
            ) {
              setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.startDate;
                return newErrors;
              });
            }
          }}
          minDate={(() => {
            const minDate = new Date();
            if (isSpecialMachineryInsuranceSlug(slug)) {
              minDate.setDate(minDate.getDate() + 1);
            }
            minDate.setHours(0, 0, 0, 0);
            return minDate;
          })()}
          maxDate={(() => {
            const maxDate = new Date();
            maxDate.setMonth(maxDate.getMonth() + 1);
            maxDate.setHours(23, 59, 59, 999);
            return maxDate;
          })()}
          error={!!validationErrors.startDate}
        />
        <div
          key={`date-error-${(validationErrors.startDate || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.startDate ? styles.errorHighlight : ''}`}
        >
          {validationErrors.startDate || ''}
        </div>
      </div>

      {/* Registruota transporto priemonė - Hide for KASKO and spec-transporto (CPM) */}
      {fields.includes('plate') &&
        !isKaskoInsuranceSlug(slug) &&
        !isSpecialMachineryInsuranceSlug(slug) &&
        !hideRegisteredVehicleToggle && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('calculator.step1.registered_vehicle')}
            </label>
            <div className={styles.radioGroup}>
              <label
                className={`${styles.radioOption} ${formData.registeredVehicle === true ? styles.radioSelected : ''}`}
              >
                <input
                  type='radio'
                  name='registeredVehicle'
                  value='true'
                  checked={formData.registeredVehicle === true}
                  onChange={e => handleRegisteredVehicleChange(true)}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>
                  {t('calculator.step1.yes')}
                </span>
              </label>
              <label
                className={`${styles.radioOption} ${formData.registeredVehicle === false ? styles.radioSelected : ''}`}
              >
                <input
                  type='radio'
                  name='registeredVehicle'
                  value='false'
                  checked={formData.registeredVehicle === false}
                  onChange={e => handleRegisteredVehicleChange(false)}
                  className={styles.radioInput}
                />
                <span className={styles.radioLabel}>
                  {t('calculator.step1.no')}
                </span>
              </label>
            </div>
          </div>
        )}
    </>
  );
}
